import React, { useEffect, useState, useRef } from "react";
import { Plus, Trash2, Save, FileSpreadsheet, Upload, TrendingUp, TrendingDown, Coins, BookmarkPlus, Bookmark, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api, { BACKEND_URL } from "@/lib/api";

/**
 * Admin-only per-event Kaimera P&L tab.
 * Auto-injects Kaimera fee revenue from event tiers; admin adds expenses and
 * optional extra incomes. Supports save-as-template + apply-template.
 */
export default function KaimeraBudgetTab({ event }) {
  const [budget, setBudget] = useState(null);
  const [incomes, setIncomes] = useState([]);
  const [expenses, setExpenses] = useState([]);
  // Independent real-time ledger — only visible when the "Real time"
  // toggle is on. Kept fully separate from the projected list above.
  const [liveIncomes, setLiveIncomes] = useState([]);
  const [liveExpenses, setLiveExpenses] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [showTplPicker, setShowTplPicker] = useState(false);
  const [showSaveTpl, setShowSaveTpl] = useState(false);
  const [tplForm, setTplForm] = useState({ name: "", description: "" });
  const [realtime, setRealtime] = useState(false);
  const [live, setLive] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    const [b, t] = await Promise.all([
      api.get(`/admin/kaimera-budgets/${event.event_id}`),
      api.get("/admin/kaimera-budget-templates"),
    ]);
    setBudget(b.data);
    setIncomes(b.data.incomes || []);
    setExpenses(b.data.expenses || []);
    setLiveIncomes(b.data.live_incomes || []);
    setLiveExpenses(b.data.live_expenses || []);
    setTemplates(t.data || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [event.event_id]);

  // Fetch live Kaimera-retained totals whenever the "Real time" toggle
  // flips on. Sourced from the same endpoint used by BudgetTab so numbers
  // agree byte-for-byte across the two dashboards.
  useEffect(() => {
    if (!realtime) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/events/${event.event_id}/budget/realtime`);
        if (!cancelled) setLive(r.data);
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || "Real-time fetch failed");
      }
    })();
    return () => { cancelled = true; };
  }, [realtime, event.event_id]);

  const persist = async (nextInc, nextExp) => {
    const body = realtime
      ? { live_incomes: nextInc, live_expenses: nextExp }
      : { incomes: nextInc, expenses: nextExp };
    const r = await api.put(`/admin/kaimera-budgets/${event.event_id}`, body);
    setBudget(r.data);
  };

  // Active arrays — swap based on the toggle.
  const activeIncomes = realtime ? liveIncomes : incomes;
  const activeExpenses = realtime ? liveExpenses : expenses;
  const setActiveIncomes = realtime ? setLiveIncomes : setIncomes;
  const setActiveExpenses = realtime ? setLiveExpenses : setExpenses;

  const addItem = async (kind, item) => {
    if (!item.label.trim()) return toast.error("Name is required");
    if (parseFloat(item.amount) <= 0 || !item.amount) return toast.error("Amount must be > 0");
    const row = {
      item_id: `kbi_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      label: item.label.trim(),
      quantity: parseFloat(item.quantity) || 1,
      amount: parseFloat(item.amount) || 0,
      notes: item.notes || "",
    };
    const nextInc = kind === "income" ? [...activeIncomes, row] : activeIncomes;
    const nextExp = kind === "expense" ? [...activeExpenses, row] : activeExpenses;
    if (kind === "income") setActiveIncomes(nextInc); else setActiveExpenses(nextExp);
    try { await persist(nextInc, nextExp); toast.success(`${kind === "income" ? "Income" : "Expense"} added`); }
    catch { toast.error("Save failed"); }
  };

  const updateItem = async (kind, idx, patch) => {
    const arr = kind === "income" ? [...activeIncomes] : [...activeExpenses];
    arr[idx] = { ...arr[idx], ...patch };
    if (kind === "income") setActiveIncomes(arr); else setActiveExpenses(arr);
    try { await persist(kind === "income" ? arr : activeIncomes, kind === "expense" ? arr : activeExpenses); }
    catch { toast.error("Save failed"); }
  };

  const delItem = async (kind, idx) => {
    const arr = (kind === "income" ? activeIncomes : activeExpenses).filter((_, i) => i !== idx);
    if (kind === "income") setActiveIncomes(arr); else setActiveExpenses(arr);
    await persist(kind === "income" ? arr : activeIncomes, kind === "expense" ? arr : activeExpenses);
  };

  const exportExcel = () => {
    const token = localStorage.getItem("munners_token");
    window.open(`${BACKEND_URL}/api/admin/kaimera-budgets/${event.event_id}/xlsx?t=${token}&_=${Date.now()}`, "_blank");
  };

  const importExcel = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post(`/admin/kaimera-budgets/${event.event_id}/import`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBudget(r.data);
      setIncomes(r.data.incomes || []);
      setExpenses(r.data.expenses || []);
      toast.success("Imported from Excel");
    } catch (e) { toast.error("Import failed: " + (e?.response?.data?.detail || e.message)); }
    if (fileRef.current) fileRef.current.value = "";
  };

  const applyTemplate = async (tid) => {
    if (!window.confirm("Apply this template? Its items will be appended to the current lists (editable after).")) return;
    try {
      const r = await api.post(`/admin/kaimera-budgets/${event.event_id}/apply-template/${tid}`);
      setBudget(r.data);
      setIncomes(r.data.incomes || []);
      setExpenses(r.data.expenses || []);
      setShowTplPicker(false);
      toast.success("Template applied");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const saveAsTemplate = async () => {
    if (!tplForm.name.trim()) return toast.error("Template name required");
    try {
      await api.post("/admin/kaimera-budget-templates", {
        name: tplForm.name.trim(),
        description: tplForm.description,
        incomes,
        expenses,
      });
      toast.success("Template saved");
      setShowSaveTpl(false); setTplForm({ name: "", description: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const delTemplate = async (tid) => {
    if (!window.confirm("Delete this template?")) return;
    await api.delete(`/admin/kaimera-budget-templates/${tid}`);
    load();
  };

  if (!budget) return <div className="text-slate-400">Loading…</div>;

  // Toggle-driven Stat overrides. When Real-time is ON:
  //   Kaimera Fee Income  → live.kaimera_retained  (from journal Cr legs)
  //   Extra Income        → sum of live_incomes    (separate ledger)
  //   Total Expenses      → sum of live_expenses   (separate ledger)
  //   Net Profit          → recomputed from the three above
  // When OFF, we use the projected numbers computed server-side.
  const useLive = realtime && live;
  const liveExtra = liveIncomes.reduce((s, it) => s + (it.amount || 0) * (it.quantity || 1), 0);
  const liveExp = liveExpenses.reduce((s, it) => s + (it.amount || 0) * (it.quantity || 1), 0);
  const shownKaimeraIncome = useLive ? (live.kaimera_retained || 0) : (budget.kaimera_fee_income || 0);
  const shownExtra = useLive ? liveExtra : (budget.extra_income || 0);
  const shownExpenses = useLive ? liveExp : (budget.expenses_total || 0);
  const shownNet = shownKaimeraIncome + shownExtra - shownExpenses;

  return (
    <div data-testid="kaimera-budget-tab">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h3 className="font-display font-bold text-xl flex items-center gap-2"><Coins className="w-5 h-5 text-orange-600" /> Kaimera P&L — Admin Only</h3>
          <p className="text-sm text-slate-500">Kaimera fee revenue is auto-computed from this event's tiers. Add Kaimera-side expenses to see the net profit.</p>
        </div>
        <label
          className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border cursor-pointer text-xs font-semibold transition ${realtime ? "bg-emerald-50 border-emerald-300 text-emerald-700" : "bg-white border-slate-200 text-slate-500 hover:border-slate-300"}`}
          title="Show Kaimera-retained income based on actual receipts + journal entries instead of the projected fee tiers"
          data-testid="kaimera-realtime-toggle"
        >
          <input
            type="checkbox" checked={realtime}
            onChange={(e) => setRealtime(e.target.checked)}
            className="w-4 h-4 accent-emerald-600"
          />
          <span className={`w-1.5 h-1.5 rounded-full ${realtime ? "bg-emerald-500 animate-pulse" : "bg-slate-300"}`}></span>
          Real time {realtime && live && <span className="text-[10px] font-normal text-slate-400">· {live.receipt_count} receipt{live.receipt_count === 1 ? "" : "s"}</span>}
        </label>
      </div>

      <div className="flex flex-wrap gap-2 mb-5">
        <button onClick={exportExcel} className="btn-ghost text-sm" data-testid="kaimera-export-btn"><FileSpreadsheet className="w-4 h-4" /> Download Excel</button>
        <label className="btn-ghost text-sm cursor-pointer" data-testid="kaimera-import-btn">
          <Upload className="w-4 h-4" /> Upload Excel
          <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => importExcel(e.target.files?.[0])} />
        </label>
        <div className="relative">
          <button onClick={() => setShowTplPicker((s) => !s)} className="btn-ghost text-sm" data-testid="kaimera-template-picker-btn"><Bookmark className="w-4 h-4" /> Apply template <ChevronDown className="w-3 h-3" /></button>
          {showTplPicker && (
            <div className="absolute z-40 top-full mt-1 right-0 bg-white rounded-xl shadow-2xl border border-slate-100 p-1 w-72 max-h-80 overflow-y-auto">
              {templates.length === 0 && <div className="text-xs text-slate-400 p-3">No templates saved yet.</div>}
              {templates.map((t) => (
                <div key={t.template_id} className="flex items-center justify-between p-2 hover:bg-slate-50 rounded-lg" data-testid={`kaimera-template-${t.template_id}`}>
                  <button onClick={() => applyTemplate(t.template_id)} className="text-left flex-1 text-sm">
                    <div className="font-bold text-slate-800">{t.name}</div>
                    {t.description && <div className="text-xs text-slate-500">{t.description}</div>}
                    <div className="text-[10px] text-slate-400">{(t.expenses || []).length} expense + {(t.incomes || []).length} income items</div>
                  </button>
                  <button onClick={() => delTemplate(t.template_id)} className="text-rose-500 p-1 hover:bg-rose-50 rounded"><Trash2 className="w-3 h-3" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
        <button onClick={() => setShowSaveTpl(true)} className="btn-ghost text-sm" data-testid="kaimera-save-template-btn"><BookmarkPlus className="w-4 h-4" /> Save as template</button>
      </div>

      <div className="grid lg:grid-cols-4 gap-4 mb-6">
        <Stat label={useLive ? "Kaimera Fee Income (live)" : "Kaimera Fee Income"} value={shownKaimeraIncome} color="emerald" sub={useLive ? `Retained from ${live.total_delegates} paid delegate(s)` : "Auto — from fee tiers"} />
        <Stat label={useLive ? "Extra Income (live)" : "Extra Income"} value={shownExtra} color="sky" sub={`${(useLive ? liveIncomes : incomes).length} items`} />
        <Stat label={useLive ? "Total Expenses (live)" : "Total Expenses"} value={shownExpenses} color="rose" sub={`${(useLive ? liveExpenses : expenses).length} items`} />
        <Stat label={useLive ? "Net Profit (live)" : "Net Profit"} value={shownNet} color={shownNet >= 0 ? "orange" : "rose"} sub={shownNet >= 0 ? "Retained by Kaimera" : "In red"} />
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <Ledger title={realtime ? "Extra Incomes (real-time)" : "Extra Incomes"} kind="income" accent="emerald" Icon={TrendingUp} items={activeIncomes}
          onAdd={(it) => addItem("income", it)} onUpdate={(i, p) => updateItem("income", i, p)} onDel={(i) => delItem("income", i)} testidPrefix="k-income"
          modeHint={realtime ? "Real-time — separate from your projected ledger." : "Projected — switch on Real time to log actual incomes separately."} />
        <Ledger title={realtime ? "Expenses (real-time)" : "Expenses"} kind="expense" accent="rose" Icon={TrendingDown} items={activeExpenses}
          onAdd={(it) => addItem("expense", it)} onUpdate={(i, p) => updateItem("expense", i, p)} onDel={(i) => delItem("expense", i)} testidPrefix="k-expense"
          modeHint={realtime ? "Real-time — separate from your projected ledger." : "Projected — switch on Real time to log actual expenses separately."} />
      </div>

      {showSaveTpl && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowSaveTpl(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="save-template-modal">
            <h3 className="font-display font-black text-xl mb-1">Save as template</h3>
            <p className="text-xs text-slate-500 mb-3">Store the current {incomes.length + expenses.length} row(s) as a reusable template.</p>
            <input placeholder="Template name (e.g. Standard MUN)" className="input-pill" value={tplForm.name} onChange={(e) => setTplForm({ ...tplForm, name: e.target.value })} data-testid="save-template-name" />
            <textarea rows={2} placeholder="Description (optional)" className="input-pill mt-2" value={tplForm.description} onChange={(e) => setTplForm({ ...tplForm, description: e.target.value })} />
            <div className="flex gap-2 mt-4">
              <button onClick={saveAsTemplate} className="btn-primary flex-1 py-2.5" data-testid="save-template-submit"><Save className="w-4 h-4" /> Save</button>
              <button onClick={() => setShowSaveTpl(false)} className="btn-ghost py-2.5 px-4">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, color, sub }) {
  const palette = {
    emerald: "from-green-50 to-emerald-50 text-emerald-700",
    sky: "from-sky-50 to-blue-50 text-sky-700",
    rose: "from-rose-50 to-pink-50 text-rose-700",
    orange: "from-orange-50 to-amber-50 text-orange-700",
  }[color] || "from-slate-50 to-slate-100 text-slate-700";
  return (
    <div className={`dashboard-card p-5 bg-gradient-to-br ${palette}`}>
      <div className="text-xs font-bold uppercase tracking-wider">{label}</div>
      <div className="text-3xl font-display font-black mt-1">₹{(value || 0).toLocaleString()}</div>
      <div className="text-xs mt-1 opacity-80">{sub}</div>
    </div>
  );
}

function Ledger({ title, kind, accent, Icon, items, onAdd, onUpdate, onDel, testidPrefix, modeHint }) {
  const [form, setForm] = useState({ label: "", quantity: 1, amount: "" });
  const [editingIdx, setEditingIdx] = useState(null);
  const [draftEdit, setDraftEdit] = useState(null);

  const submit = (e) => {
    e.preventDefault();
    onAdd(form);
    setForm({ label: "", quantity: 1, amount: "" });
  };
  const startEdit = (i) => { setEditingIdx(i); setDraftEdit({ ...items[i] }); };
  const cancelEdit = () => { setEditingIdx(null); setDraftEdit(null); };
  const commitEdit = async (i) => {
    await onUpdate(i, { label: draftEdit.label, quantity: parseFloat(draftEdit.quantity) || 1, amount: parseFloat(draftEdit.amount) || 0 });
    cancelEdit();
  };

  const accentBar = { emerald: "border-emerald-500", rose: "border-rose-500" }[accent] || "border-slate-400";
  const accentText = { emerald: "text-emerald-700", rose: "text-rose-700" }[accent] || "text-slate-700";
  const total = items.reduce((s, it) => s + (it.amount * it.quantity), 0);

  return (
    <div className="dashboard-card p-5" data-testid={`ledger-${kind}`}>
      <div className="flex items-center justify-between mb-1">
        <h4 className="font-display font-bold text-lg flex items-center gap-2"><Icon className={`w-4 h-4 ${accentText}`} /> {title}</h4>
        <div className={`text-sm font-bold ${accentText}`}>₹{total.toLocaleString()}</div>
      </div>
      {modeHint && <p className="text-[11px] text-slate-400 mb-3">{modeHint}</p>}

      <form onSubmit={submit} className={`p-3 rounded-xl bg-slate-50 mb-3 border-l-4 ${accentBar}`}>
        <div className="grid grid-cols-12 gap-2">
          <input placeholder={kind === "income" ? "e.g. Consulting fee" : "e.g. Marketing spend"} className="input-pill py-2 text-sm col-span-6" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid={`${testidPrefix}-label-input`} />
          <input type="number" min={1} placeholder="Qty" className="input-pill py-2 text-sm col-span-2" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
          <input type="number" min={0} placeholder="Value ₹" className="input-pill py-2 text-sm col-span-3" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid={`${testidPrefix}-amount-input`} />
          <button type="submit" className="btn-primary col-span-1 py-2 px-0" data-testid={`${testidPrefix}-add-btn`}><Plus className="w-4 h-4" /></button>
        </div>
      </form>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-slate-500 border-b border-slate-100">
              <th className="py-2 pl-1">Name</th>
              <th className="py-2 text-right w-16">Qty</th>
              <th className="py-2 text-right w-24">Value ₹</th>
              <th className="py-2 text-right w-28">Subtotal</th>
              <th className="w-16"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan="5" className="text-center text-slate-400 py-6 text-sm">No {kind}s yet.</td></tr>}
            {items.map((it, i) => (
              editingIdx === i ? (
                <tr key={it.item_id || i} className="bg-orange-50">
                  <td className="py-1.5 pl-1"><input className="input-pill py-1 text-xs" value={draftEdit.label} onChange={(e) => setDraftEdit({ ...draftEdit, label: e.target.value })} /></td>
                  <td><input type="number" className="input-pill py-1 text-xs w-full" value={draftEdit.quantity} onChange={(e) => setDraftEdit({ ...draftEdit, quantity: e.target.value })} /></td>
                  <td><input type="number" className="input-pill py-1 text-xs w-full" value={draftEdit.amount} onChange={(e) => setDraftEdit({ ...draftEdit, amount: e.target.value })} /></td>
                  <td className={`text-right font-semibold ${accentText}`}>₹{((parseFloat(draftEdit.amount) || 0) * (parseFloat(draftEdit.quantity) || 1)).toLocaleString()}</td>
                  <td className="text-right">
                    <button onClick={() => commitEdit(i)} className="text-emerald-600 text-xs font-bold mr-1">✓</button>
                    <button onClick={cancelEdit} className="text-slate-400 text-xs">✕</button>
                  </td>
                </tr>
              ) : (
                <tr key={it.item_id || i} className="border-b border-slate-50 hover:bg-slate-50/40">
                  <td className="py-2 pl-1 cursor-pointer" onClick={() => startEdit(i)}>
                    <div className="font-medium text-slate-800">{it.label}</div>
                    {it.notes && <div className="text-[10px] text-slate-400">{it.notes}</div>}
                  </td>
                  <td className="py-2 text-right text-slate-600">{it.quantity}</td>
                  <td className="py-2 text-right text-slate-600">₹{(it.amount || 0).toLocaleString()}</td>
                  <td className={`py-2 text-right font-semibold ${accentText}`}>₹{((it.amount || 0) * (it.quantity || 1)).toLocaleString()}</td>
                  <td className="py-2 text-right">
                    <button onClick={() => startEdit(i)} className="text-xs text-orange-600 hover:underline mr-2">edit</button>
                    <button onClick={() => onDel(i)} className="text-rose-500"><Trash2 className="w-3.5 h-3.5 inline" /></button>
                  </td>
                </tr>
              )
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
